import React, { useContext, useEffect, useMemo, useState } from 'react';

import Cell from 'components/V5Gadget/Matrix/Cell';
import Loading from 'components/Loading';
import Tooltip from 'components/Tooltip';
import BonusPeriodsContext from 'contexts/BonusPeriodsContext';
import CloudFunctionClientContext from 'contexts/CloudFunctionClientContext';
import resolveDriverRewardOutput from 'components/DriverAX/utils/resolveDriverRewardOutput';
import useCurrentAccountId from 'hooks/useCurrentAccountId';
import useValueFormatters from 'hooks/useValueFormatters';
import { FleetopsGridCellRendererParams } from 'types/agGrid';

import GridCellErrorBoundary from '../../GridCellErrorBoundary';
import useBonusCellBonusProgram from '../hooks/useBonusCellBonusProgram';
import {
  getRewards,
  isArnoldBrosRewardBands,
  isGeneralRewardOutput,
} from '../utils/rewardBandsUtils';
import computeScoreColor from '../utils/bonusScoreColourUtils';
import {
  AggregatedRewardBandsBlock,
  ArnoldBrosComponentBlock,
  ArnoldBrosCombinedBlock,
  MultiplierBandedBlock,
  OuterDiv,
} from './RewardBandsTooltip';

// fuelScore → fuel, operationsScore → operations, safetyScore → safety
const getRewardNameFromScoreField = (scoreField: string): string =>
  scoreField.replace(/Score$/, '').toLowerCase();

// True when the reward carries a band ladder worth showing in a tooltip. Covers
// both the frozen pre-3.0.0 Arnold shape (`rewardBands.components`) and the v3.x
// shape (`aggregated` children, or a flat `multiplierBanded` `rewardBands.bands`).
// When there is nothing to show the cell must not render a tooltip at all (an
// empty tooltip still draws a stray arrow). See ENG-944.
export const hasBandContent = (
  matchingReward: BonusTypes.Rewards.AnyRewardOutput | undefined,
): boolean => {
  if (!matchingReward) {
    return false;
  }
  const rewardBands = matchingReward.rewardBands;
  // Pre-3.0.0 Arnold: nested component ladders.
  if (rewardBands && isArnoldBrosRewardBands(rewardBands)) {
    return rewardBands.components.length > 0;
  }
  if (!isGeneralRewardOutput(matchingReward)) {
    return false;
  }
  // v3.x aggregated (e.g. fuel): band ladders live on the children.
  if (matchingReward.type === 'aggregated') {
    return (matchingReward.rewards ?? []).some(
      (child) => (child.rewardBands?.bands?.length ?? 0) > 0,
    );
  }
  // v3.x multiplierBanded: a single flat band ladder.
  return (matchingReward.rewardBands?.bands?.length ?? 0) > 0;
};

// Renders the band ladder for a score reward. Dispatches on the reward shape so
// both the frozen pre-3.0.0 Arnold `components` shape and the v3.x
// `multiplierBanded` / `aggregated` shapes render. BOTH branches are still hit
// by live data — historical periods are frozen at their old config version and
// keep returning the `components` shape, so neither path is dead (see the
// LEGACY / FROZEN note in RewardBandsTooltip.tsx). Guarded so a simplified /
// absent band shape degrades to nothing instead of throwing (a throw here would
// be swallowed by the surrounding `isHiddenError` grid cell error boundary and
// blank the whole cell — value included). See ENG-944.
export const BonusScoreBandsTooltip = ({
  matchingReward,
  dataset,
  rewardType,
}: {
  matchingReward: BonusTypes.Rewards.AnyRewardOutput;
  dataset: string;
  rewardType: 'none' | 'points' | 'monetary';
}) => {
  const rewardBands = matchingReward.rewardBands;

  // Pre-3.0.0 Arnold: nested component ladders.
  if (rewardBands && isArnoldBrosRewardBands(rewardBands)) {
    const { components } = rewardBands;
    if (components.length === 0) {
      return null;
    }
    return (
      <OuterDiv>
        {components.length > 1 && (
          <ArnoldBrosCombinedBlock
            rewardBands={rewardBands}
            dataset={dataset}
          />
        )}
        {components.map((component) => (
          <ArnoldBrosComponentBlock
            key={component.field}
            component={component}
            dataset={dataset}
          />
        ))}
      </OuterDiv>
    );
  }

  if (!isGeneralRewardOutput(matchingReward)) {
    return null;
  }

  // v3.x aggregated (e.g. fuel): combined header + each child's ladder.
  if (
    matchingReward.type === 'aggregated' &&
    (matchingReward.rewards ?? []).length > 0
  ) {
    return (
      <OuterDiv>
        <AggregatedRewardBandsBlock
          reward={matchingReward}
          dataset={dataset}
          rewardType={rewardType}
        />
      </OuterDiv>
    );
  }

  // v3.x multiplierBanded: a single flat band ladder.
  if ((matchingReward.rewardBands?.bands?.length ?? 0) > 0) {
    return (
      <OuterDiv>
        <MultiplierBandedBlock
          reward={matchingReward}
          dataset={dataset}
          rewardType={rewardType}
        />
      </OuterDiv>
    );
  }

  return null;
};

const LoadingTooltip = () => (
  <div
    style={{
      backgroundColor: 'var(--color-bg-primary)',
      padding: 16,
      border: '1px solid var(--color-border-tertiary)',
      borderRadius: 6,
    }}
  >
    <Loading />
  </div>
);

// Lazily fetches the driver's reward output once `enabled` (the user has hovered
// the cell) and resolves the matching score reward. Kept out of the tooltip
// content so the fetch starts on hover-intent — before the tooltip's open delay
// elapses — letting the cell decide whether to show a tooltip at all.
const useBonusScoreReward = ({
  driver,
  scoreField,
  enabled,
}: {
  driver?: string;
  scoreField: string;
  enabled: boolean;
}) => {
  const { selectedBonusPeriod, selectedSubPeriod } =
    useContext(BonusPeriodsContext);
  const bonusProgram = useBonusCellBonusProgram();
  const { api } = useContext(CloudFunctionClientContext);
  const accountId = useCurrentAccountId();

  const [isLoading, setIsLoading] = useState(false);
  const [rewardOutput, setRewardOutput] = useState<
    BonusTypes.Rewards.AnyDriverRewardsOutput | undefined
  >(undefined);

  useEffect(() => {
    if (!enabled || !driver || !bonusProgram || !selectedBonusPeriod) {
      return;
    }
    let isActive = true;
    setIsLoading(true);
    resolveDriverRewardOutput({
      api,
      accountId,
      driver,
      bonusProgramId: bonusProgram.id,
      bonusPeriodId: selectedBonusPeriod.id,
      subPeriod: selectedSubPeriod,
    }).then((newRewardOutput) => {
      if (!isActive) {
        return;
      }
      setRewardOutput(newRewardOutput);
      setIsLoading(false);
    });
    return () => {
      isActive = false;
    };
  }, [
    accountId,
    api,
    bonusProgram,
    driver,
    enabled,
    selectedBonusPeriod,
    selectedSubPeriod,
  ]);

  const matchingReward = useMemo(():
    | BonusTypes.Rewards.AnyRewardOutput
    | undefined => {
    if (!rewardOutput) {
      return undefined;
    }
    const rewardName = getRewardNameFromScoreField(scoreField);
    return getRewards(rewardOutput).find((r) => r.name === rewardName);
  }, [rewardOutput, scoreField]);

  return { isLoading, matchingReward };
};

export const BonusScoreCell = ({
  elasticDocument,
  dataset,
  scoreField,
  colourConfig,
  rewardType,
}: {
  elasticDocument: ElasticDocument;
  dataset: string;
  scoreField: string;
  colourConfig?: FleetOps.BonusScoreColourConfig;
  rewardType: 'none' | 'points' | 'monetary';
}) => {
  const { formatField } = useValueFormatters();

  const driver = useMemo(
    () => elasticDocument['driver'] as string | undefined,
    [elasticDocument],
  );

  const formattedValue = useMemo(
    () =>
      formatField({
        field: scoreField,
        value: elasticDocument[scoreField],
        dataset,
      }),
    [dataset, elasticDocument, formatField, scoreField],
  );

  const scoreColor = useMemo(
    () =>
      computeScoreColor({
        value: elasticDocument[scoreField],
        colourConfig,
      }),
    [colourConfig, elasticDocument, scoreField],
  );

  // Fetch the reward only once the user shows intent by hovering the cell — the
  // fetch feeds the decision of whether to show a tooltip at all.
  const [isHovered, setIsHovered] = useState(false);
  const { isLoading, matchingReward } = useBonusScoreReward({
    driver,
    scoreField,
    enabled: isHovered,
  });

  // Only give the Tooltip content when there is a band ladder to show. When the
  // content is undefined the Tooltip collapses to the bare value (no floating
  // box, no arrow) — so band-simplified current-period cells show no tooltip
  // rather than a stray arrow. See ENG-944.
  const tooltipContent = useMemo(() => {
    if (!isHovered || !driver) {
      return undefined;
    }
    if (isLoading) {
      return <LoadingTooltip />;
    }
    if (matchingReward && hasBandContent(matchingReward)) {
      return (
        <BonusScoreBandsTooltip
          matchingReward={matchingReward}
          dataset={dataset}
          rewardType={rewardType}
        />
      );
    }
    return undefined;
  }, [dataset, driver, isHovered, isLoading, matchingReward, rewardType]);

  return (
    <Cell rightAlign>
      <Tooltip
        isUnstyledTooltip
        hideDelay={300}
        content={tooltipContent}
        position={'left'}
      >
        <span
          onMouseEnter={() => setIsHovered(true)}
          style={{
            color: scoreColor,
            fontWeight: scoreColor ? 600 : undefined,
            cursor: 'help',
          }}
        >
          {formattedValue}
        </span>
      </Tooltip>
    </Cell>
  );
};

const BonusScoreCellProvider = (params: FleetopsGridCellRendererParams) => {
  const bonusProgram = useBonusCellBonusProgram();

  const elasticDocument = useMemo(
    () => params.node.data as ElasticDocument,
    [params],
  );

  const field = useMemo(() => {
    const { colDef } = params;
    if (!colDef || !colDef.field) {
      return undefined;
    }
    return colDef.field;
  }, [params]);

  const colourConfig =
    params.colDef.cellRendererParams.fieldView?.bonusScoreColourConfig;

  if (!bonusProgram || !field) {
    return null;
  }

  return (
    <BonusScoreCell
      elasticDocument={elasticDocument}
      dataset={bonusProgram.dataset}
      scoreField={field}
      colourConfig={colourConfig}
      rewardType={bonusProgram.rewardType}
    />
  );
};

const Gate = (params: FleetopsGridCellRendererParams) => (
  <GridCellErrorBoundary
    params={params}
    childComponent={<BonusScoreCellProvider {...params} />}
  />
);

export default Gate;
